import React from "react";
import PropTypes from "prop-types";
import moment from "moment";
import Page from "../../../components/BaseComponents/Page/Page";
import {FaSolidIcon} from "../../../components/FontAwesomeIcons";
import {PageFooter} from "../../../components/BaseComponents/Page/PageFooter";
import {CreateEventTypeStep} from "./CreateEventTypeStep";
import {CreateEventDetailsStep} from "./CreateEventDetailsStep";
import {CreateEventParticipantsStep} from "./CreateEventParticipantsStep";
import {CreateEventOptionsStep} from "./CreateEventOptionsStep";
import {
    USER_EVENT_THEME_COLORS,
    USER_EVENT_THEME_FONTS,
    USER_EVENT_MAX_DURATION_DAYS,
    USER_EVENT_TYPE_PHOTO_ALBUM,
    USER_EVENT_TYPE_RESERVATIONS
} from "./CreateEventConstants";
import {CreateEventStepProgress} from "./CreateEventStepProgress";
import {swal, swalError, swalLoading, swalSuccess} from "../../../helpers/SweetAlertWrapper";
import {CreateEventLandingStyleStep} from "./CreateEventLandingStyleStep";
import AuthAPI from "../../../api/AuthAPI";
import {UsersModuleEventsAPI} from "../../../api/UsersModuleEvents/UsersModuleEventsAPI";
import {UsersModulePhotoAlbumsAPI} from "../../../api/UsersModulePhotoAlbums/UsersModulePhotoAlbumsAPI";

const CREATE_EVENT_STEPS = {
    TYPE: "type",
    DETAILS: "details",
    PARTICIPANTS: "participants",
    OPTIONS: "options",
    LANDING_STYLE: "landingStyle",
};

export class CreateEventPage extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            currentStep: CREATE_EVENT_STEPS.TYPE,
            isSaving: false,
            eventData: {
                type: null,
                cover: null,
                name: "",
                startDate: "",
                endDate: "",
                placeName: "",
                address: "",
                latitude: "",
                longitude: "",
                directions: "",
                description: "",
                participantsNameMode: "all",
                expectedParticipantsEnabled: false,
                expectedParticipants: "",
                maxParticipantsPerReservationEnabled: false,
                maxParticipantsPerReservation: "",
                showIntolerances: false,
                showNotes: false,
                guestsApprovalMode: "confirmed",
                externalLinksEnabled: false,
                externalLinks: [],
                photoAlbumEnabled: false,
                themeColor: USER_EVENT_THEME_COLORS[0].value,
                themeFont: Object.keys(USER_EVENT_THEME_FONTS)[0],
            }
        };
        this.progressContainerStyle = {
            height: "6px",
        };
    }

    handleTypeChoose = type => {
        this.setState({
            eventData: {
                ...this.state.eventData,
                type: type,
            }
        }, this.handleNextStep);
    }

    handleDataChange = field => value => {
        this.setState({
            eventData: {
                ...this.state.eventData,
                [field]: value,
            }
        });
    }

    handleCheckboxChange = field => e => {
        this.handleDataChange(field)(e.target.checked);
    }

    handleInputChange = field => e => {
        this.handleDataChange(field)(e.target.value);
    }

    handleLinksChange = links => {
        this.setState({
            eventData: {
                ...this.state.eventData,
                externalLinks: links,
            }
        });
    }

    handleNextStep = () => {
        if (this.state.currentStep === CREATE_EVENT_STEPS.TYPE) {
            this.setState({currentStep: CREATE_EVENT_STEPS.DETAILS});
            return;
        }
        if (this.state.currentStep === CREATE_EVENT_STEPS.DETAILS &&
            this.state.eventData.type === USER_EVENT_TYPE_RESERVATIONS) {
            if (!this.isDetailsStepValid()) {
                this.showMissingFieldsAlert();
                return;
            }
            this.setState({currentStep: CREATE_EVENT_STEPS.PARTICIPANTS});
            return;
        }
        if (this.state.currentStep === CREATE_EVENT_STEPS.DETAILS) {
            if (!this.isDetailsStepValid()) {
                this.showMissingFieldsAlert();
                return;
            }
            this.setState({currentStep: CREATE_EVENT_STEPS.LANDING_STYLE});
            return;
        }
        if (this.state.currentStep === CREATE_EVENT_STEPS.PARTICIPANTS) {
            if (!this.isParticipantsStepValid()) {
                this.showParticipantsValidationAlert();
                return;
            }
            this.setState({currentStep: CREATE_EVENT_STEPS.OPTIONS});
            return;
        }
        if (this.state.currentStep === CREATE_EVENT_STEPS.OPTIONS) {
            this.setState({currentStep: CREATE_EVENT_STEPS.LANDING_STYLE});
        }
    }

    handlePreviousStep = _ => {
        if (this.state.currentStep === CREATE_EVENT_STEPS.DETAILS) {
            this.setState({currentStep: CREATE_EVENT_STEPS.TYPE});
            return;
        }
        if (this.state.currentStep === CREATE_EVENT_STEPS.PARTICIPANTS) {
            this.setState({currentStep: CREATE_EVENT_STEPS.DETAILS});
            return;
        }
        if (this.state.currentStep === CREATE_EVENT_STEPS.OPTIONS) {
            this.setState({currentStep: CREATE_EVENT_STEPS.PARTICIPANTS});
            return;
        }
        if (this.state.currentStep === CREATE_EVENT_STEPS.LANDING_STYLE) {
            this.setState({
                currentStep: this.state.eventData.type === USER_EVENT_TYPE_RESERVATIONS ?
                    CREATE_EVENT_STEPS.OPTIONS :
                    CREATE_EVENT_STEPS.DETAILS,
            });
        }
    }

    getTotalSteps = _ => this.state.eventData.type === USER_EVENT_TYPE_RESERVATIONS ? 4 : 2

    getCurrentStepNumber = _ => {
        switch (this.state.currentStep) {
            case CREATE_EVENT_STEPS.DETAILS:
                return 1;
            case CREATE_EVENT_STEPS.PARTICIPANTS:
                return 2;
            case CREATE_EVENT_STEPS.OPTIONS:
                return 3;
            case CREATE_EVENT_STEPS.LANDING_STYLE:
                return this.state.eventData.type === USER_EVENT_TYPE_RESERVATIONS ? 4 : 2;
            default:
                return 0;
        }
    }

    canShowStepProgress = _ => this.state.currentStep !== CREATE_EVENT_STEPS.TYPE

    getTodayDateValue = _ => {
        const now = new Date();
        const month = `${now.getMonth() + 1}`.padStart(2, "0");
        const day = `${now.getDate()}`.padStart(2, "0");
        return `${now.getFullYear()}-${month}-${day}`;
    }

    getDetailsMissingFields = _ => {
        const missingFields = [];
        if (!this.state.eventData.name.trim()) {
            missingFields.push("Nome evento");
        }
        if (!this.state.eventData.startDate) {
            missingFields.push("Inizio evento");
        }
        if (!this.state.eventData.endDate) {
            missingFields.push("Fine evento");
        }
        if (this.state.eventData.startDate &&
            this.state.eventData.startDate.substring(0, 10) < this.getTodayDateValue()) {
            missingFields.push("Data evento non precedente a oggi");
        }
        if (this.state.eventData.startDate &&
            this.state.eventData.endDate &&
            this.state.eventData.endDate <= this.state.eventData.startDate) {
            missingFields.push("Fine evento successiva all'inizio");
        }
        if (this.isEventDurationTooLong()) {
            missingFields.push(`Durata evento massima ${USER_EVENT_MAX_DURATION_DAYS} giorni`);
        }
        return missingFields;
    }

    isDetailsStepValid = _ => !this.getDetailsMissingFields().length

    isEventDurationTooLong = _ => {
        if (!this.state.eventData.startDate || !this.state.eventData.endDate) {
            return false;
        }

        const startDate = moment(this.state.eventData.startDate);
        const endDate = moment(this.state.eventData.endDate);
        if (!startDate.isValid() || !endDate.isValid() || !endDate.isAfter(startDate)) {
            return false;
        }

        return endDate.diff(startDate, "days", true) > USER_EVENT_MAX_DURATION_DAYS;
    }

    getParticipantsValidationErrors = _ => {
        const validationErrors = [];
        const expectedParticipants = parseInt(this.state.eventData.expectedParticipants, 10);
        const maxParticipantsPerReservation = parseInt(this.state.eventData.maxParticipantsPerReservation, 10);

        if (this.state.eventData.expectedParticipantsEnabled &&
            this.state.eventData.maxParticipantsPerReservationEnabled &&
            !Number.isNaN(expectedParticipants) &&
            !Number.isNaN(maxParticipantsPerReservation) &&
            maxParticipantsPerReservation > expectedParticipants) {
            validationErrors.push("I partecipanti per conferma non possono superare i partecipanti totali");
        }
        return validationErrors;
    }

    isParticipantsStepValid = _ => !this.getParticipantsValidationErrors().length

    hasMaxParticipantsPerReservationError = _ => this.getParticipantsValidationErrors()
        .includes("I partecipanti per conferma non possono superare i partecipanti totali")

    canShowNextButton = _ => this.state.currentStep === CREATE_EVENT_STEPS.DETAILS ||
        (this.state.eventData.type === USER_EVENT_TYPE_RESERVATIONS &&
            (this.state.currentStep === CREATE_EVENT_STEPS.PARTICIPANTS ||
                this.state.currentStep === CREATE_EVENT_STEPS.OPTIONS))

    canShowSaveButton = _ => this.state.currentStep === CREATE_EVENT_STEPS.LANDING_STYLE

    formatSubmitDateTime = date => date ? date.replace("T", " ") : ""

    parseOptionalInt = value => value !== "" && value !== null && value !== undefined ? parseInt(value, 10) : null

    getReservationDefaultStatus = _ => this.state.eventData.guestsApprovalMode === "confirmed" ? "confirmed" : "pending"

    getPhotoAlbumThemeColor = _ => {
        const selectedColor = USER_EVENT_THEME_COLORS.find(color => {
            return color.value === this.state.eventData.themeColor || color.key === this.state.eventData.themeColor;
        });
        return selectedColor ? selectedColor.key : USER_EVENT_THEME_COLORS[0].key;
    }

    appendLocationFormData = formData => {
        const latitude = this.state.eventData.latitude !== "" ? parseFloat(this.state.eventData.latitude) : null
        const longitude = this.state.eventData.longitude !== "" ? parseFloat(this.state.eventData.longitude) : null
        formData.append("location[name]", this.state.eventData.placeName);
        formData.append("location[address]", this.state.eventData.address);
        formData.append("location[latitude]", latitude !== null ? latitude : "");
        formData.append("location[longitude]", longitude !== null ? longitude : "");
        formData.append("location[directions]", this.state.eventData.directions);
    }

    appendCoverFormData = async formData => {
        if (!this.state.eventData.cover) {
            return true;
        }

        let coverObjectUrl = null;
        try {
            coverObjectUrl = URL.createObjectURL(this.state.eventData.cover);
            const coverBlob = await fetch(coverObjectUrl).then(response => response.blob());
            formData.append("image", coverBlob);
            URL.revokeObjectURL(coverObjectUrl);
            return true;
        } catch (error) {
            if (coverObjectUrl) {
                URL.revokeObjectURL(coverObjectUrl);
            }
            swal.safeClose();
            swalError("Non è stato possibile leggere l'immagine caricata.");
            this.setState({isSaving: false});
            return false;
        }
    }

    /**
     * Salva i dati se l'evento è quello di raccolta prenotazioni
     */
    getReservationsFormData = async _ => {
        const selectedFont = USER_EVENT_THEME_FONTS[this.state.eventData.themeFont] || USER_EVENT_THEME_FONTS.roboto;
        const formData = new FormData();
        formData.append("token", AuthAPI.getUserAccessToken());
        formData.append("event_type", this.state.eventData.type);
        formData.append("name", this.state.eventData.name);
        formData.append("description", this.state.eventData.description);
        formData.append("start_date", this.formatSubmitDateTime(this.state.eventData.startDate));
        formData.append("end_date", this.formatSubmitDateTime(this.state.eventData.endDate));
        this.appendLocationFormData(formData);
        formData.append("settings[landing_font_family]", selectedFont.label);
        formData.append("settings[landing_primary_color]", this.state.eventData.themeColor);
        formData.append("settings[landing_secondary_color]", "#ffffff");
        formData.append("settings[landing_background_color]", "#ffffff");

        if (this.state.eventData.eventId) {
            formData.append("event_id", this.state.eventData.eventId);
        }

        formData.append("expected_participants", this.state.eventData.expectedParticipantsEnabled ?
            this.parseOptionalInt(this.state.eventData.expectedParticipants) || 0 : 0);
        formData.append("max_participants_per_reservation", this.state.eventData.maxParticipantsPerReservationEnabled ?
            this.parseOptionalInt(this.state.eventData.maxParticipantsPerReservation) || 0 : 0);
        formData.append("settings[collect_all_participants_names]", this.state.eventData.participantsNameMode === "all" ? 1 : 0);
        formData.append("settings[show_intolerances]", this.state.eventData.showIntolerances ? 1 : 0);
        formData.append("settings[show_notes]", this.state.eventData.showNotes ? 1 : 0);
        formData.append("settings[reservation_default_status]", this.getReservationDefaultStatus());
        this.state.eventData.externalLinks.forEach((link, index) => {
            formData.append(`links[${index}][name]`, link.label);
            formData.append(`links[${index}][url]`, link.url);
        });

        return await this.appendCoverFormData(formData) ? formData : null;
    }

    /**
     * Salva i dati se si tratta di un photoalbum
     */
    getPhotoAlbumFormData = async _ => {
        const formData = new FormData();
        formData.append("token", AuthAPI.getUserAccessToken());
        formData.append("name", this.state.eventData.name);
        formData.append("description", this.state.eventData.description);
        formData.append("start_date", this.formatSubmitDateTime(this.state.eventData.startDate));
        formData.append("end_date", this.formatSubmitDateTime(this.state.eventData.endDate));
        this.appendLocationFormData(formData);
        formData.append("settings[allow_uploads]", 1);
        formData.append("settings[require_username]", 1);
        formData.append("settings[allow_descriptions]", 1);
        formData.append("settings[require_approval]", 0);
        formData.append("settings[private_photos]", 0);
        formData.append("settings[theme_color]", this.getPhotoAlbumThemeColor());
        formData.append("settings[theme_font]", this.state.eventData.themeFont);

        if (this.state.eventData.eventAlbumId) {
            formData.append("event_album_id", this.state.eventData.eventAlbumId);
        }
        if (this.state.eventData.backgroundImageUrl) {
            formData.append("background_image_url", this.state.eventData.backgroundImageUrl);
        }

        return await this.appendCoverFormData(formData) ? formData : null;
    }

    getSaveRequest = _ => this.state.eventData.type === USER_EVENT_TYPE_PHOTO_ALBUM ?
        UsersModulePhotoAlbumsAPI.Admin.Album.save :
        UsersModuleEventsAPI.Admin.Events.save

    getSubmitFormData = async _ => this.state.eventData.type === USER_EVENT_TYPE_PHOTO_ALBUM ?
        this.getPhotoAlbumFormData() :
        this.getReservationsFormData()

    handleSaveAndShare = async _ => {
        if (!this.isDetailsStepValid()) {
            this.showMissingFieldsAlert();
            return;
        }

        if (this.state.eventData.type === USER_EVENT_TYPE_RESERVATIONS && !this.isParticipantsStepValid()) {
            this.showParticipantsValidationAlert();
            return;
        }

        this.setState({isSaving: true});
        swalLoading("Salvataggio evento in corso...");

        const formData = await this.getSubmitFormData();
        if (!formData) {
            return;
        }

        this.getSaveRequest()(formData)
            .then(response => {
                swal.safeClose();
                swalSuccess(response.message || "Evento salvato correttamente.").then(_ => {
                    this.setState({isSaving: false});
                    this.props.onCreated(this.state.eventData.type);
                    this.props.onDismiss();
                })
            })
            .catch(error => {
                swal.safeClose();
                swalError(error);
                this.setState({isSaving: false});
            })
    }

    showMissingFieldsAlert = _ => {
        const missingFields = this.getDetailsMissingFields();
        if (!missingFields.length) {
            return;
        }
        swalError(
            <div className={"text-left"}>
                <div className={"font-weight-bold mb-2"}>Per proseguire compila:</div>
                <ul className={"mb-0 pl-3"}>
                    {missingFields.map(field => <li key={field}>{field}</li>)}
                </ul>
            </div>
        );
    }

    showParticipantsValidationAlert = _ => {
        const validationErrors = this.getParticipantsValidationErrors();
        if (!validationErrors.length) {
            return;
        }
        swalError(
            <div className={"text-left"}>
                <div className={"font-weight-bold mb-2"}>Controlla i partecipanti:</div>
                <ul className={"mb-0 pl-3"}>
                    {validationErrors.map(error => <li key={error}>{error}</li>)}
                </ul>
            </div>
        );
    }

    render() {
        return (
            <Page
                title={"Crea evento"}
                leftIcon={<FaSolidIcon name={"chevron-left"}/>}
                onLeftIconClick={this.props.onDismiss}
                active={true}
            >
                {
                    this.canShowStepProgress() &&
                    <CreateEventStepProgress
                        currentStep={this.getCurrentStepNumber()}
                        totalSteps={this.getTotalSteps()}
                        onBack={this.handlePreviousStep}
                        progressContainerStyle={this.progressContainerStyle}
                    />
                }

                <div>
                    {
                        this.state.currentStep === CREATE_EVENT_STEPS.TYPE &&
                        <CreateEventTypeStep
                            onChoose={this.handleTypeChoose}
                        />
                    }

                    {
                        this.state.currentStep === CREATE_EVENT_STEPS.DETAILS &&
                        <CreateEventDetailsStep
                            eventData={this.state.eventData}
                            onInputChange={this.handleInputChange}
                            onDataChange={this.handleDataChange}
                            minEventDate={this.getTodayDateValue()}
                        />
                    }

                    {
                        this.state.currentStep === CREATE_EVENT_STEPS.PARTICIPANTS &&
                        <CreateEventParticipantsStep
                            eventData={this.state.eventData}
                            onCheckboxChange={this.handleCheckboxChange}
                            onInputChange={this.handleInputChange}
                            onDataChange={this.handleDataChange}
                            hasMaxParticipantsPerReservationError={this.hasMaxParticipantsPerReservationError()}
                        />
                    }

                    {
                        this.state.currentStep === CREATE_EVENT_STEPS.OPTIONS &&
                        <CreateEventOptionsStep
                            eventData={this.state.eventData}
                            onCheckboxChange={this.handleCheckboxChange}
                            onLinksChange={this.handleLinksChange}
                        />
                    }

                    {
                        this.state.currentStep === CREATE_EVENT_STEPS.LANDING_STYLE &&
                        <CreateEventLandingStyleStep
                            eventData={this.state.eventData}
                            onDataChange={this.handleDataChange}
                        />
                    }
                </div>

                {
                    this.canShowNextButton() &&
                    <PageFooter className={"bg-white border-top shadow p-3"}>
                        <button
                            type={"button"}
                            className={"btn btn-primary btn-lg btn-block"}
                            onClick={this.handleNextStep}
                        >
                            Avanti <FaSolidIcon name={"chevron-right"} className={"ml-1"}/>
                        </button>
                    </PageFooter>
                }

                {
                    this.canShowSaveButton() &&
                    <PageFooter className={"bg-white border-top shadow p-3"}>
                        <button
                            type={"button"}
                            className={"btn btn-primary btn-lg btn-block"}
                            onClick={this.handleSaveAndShare}
                            disabled={this.state.isSaving}
                        >
                            Salva e condividi <FaSolidIcon name={"share-alt"} className={"ml-1"}/>
                        </button>
                    </PageFooter>
                }
            </Page>
        );
    }
}

CreateEventPage.propTypes = {
    onCreated: PropTypes.func,
    onDismiss: PropTypes.func,
};
